import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { fromJS } from "immutable";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import GridTable from "../grid-v2/GridTable";
import { SquareKanban } from "lucide-react";
import ConfirmDialog from "../dialog/ConfirmDialog";
import OctagonAlertIcon from "../icons/OctagonAlertIcon";
import TriangleAlertIcon from "../icons/TriangleAlertIcon";
import { SelectField } from "../components/ui";
import useUpgradePlan from "./hooks/useUpgradePlan";
import { deleteProject } from "../api/ProjectApi";
import TenantAction from "../actions/TenantAction";
import LayoutAction from "../actions/LayoutAction";

const PipelineSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { invalidMaxPipelines } = useUpgradePlan();

  const currentUser = useSelector((state: any) => state.userReducer.get("currentUser"));
  const isAdmin = currentUser.get("admin");
  const teamPipelines =
    useSelector((state: any) => state.tenant.get("teamPipelines")) || fromJS([]);
  const teamMembers = useSelector((state: any) => state.tenant.get("teamMembers")) || fromJS([]);

  const [currencyList, setCurrencyList] = useState<any[]>([]);
  const [currency, setCurrency] = useState<string>("");

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(searchText);

  const [showConfirm, setShowConfirm] = useState<any>({
    show: false,
    title: "",
    description: "",
  });

  const pipelineHeaders = [
    { key: "pipeline", title: "pipeline" },
    { key: "currency", title: "currency" },
    { key: "lead", title: "lead" },
    { key: "members", title: "members" },
    { key: "action", title: "" },
  ];

  useEffect(() => {
    dispatch(TenantAction.getTeamMembers());
  }, [dispatch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 800);
    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    const currencyDatas = [];
    teamPipelines.toJS().map((item) => {
      if (!currencyDatas.some((obj) => obj.value === item.currency)) {
        currencyDatas.push({
          value: item.currency,
          label: item.currency,
        });
      }
    });
    setCurrencyList(currencyDatas);
  }, [teamPipelines]);

  const handAddPipeline = () => {
    const totalPipelines = teamPipelines.size;
    if (invalidMaxPipelines(totalPipelines)) return;
    navigate("/workspaces/new");
  };

  const handleRemovePipeline = (projectId) => {
    setShowConfirm({
      show: true,
      title: t("Remove Pipeline"),
      description: t("Are you sure you want to remove this pipeline?"),
      alertIcon: <TriangleAlertIcon />,
      dangerBtn: true,
      type: "REMOVE_PIPELINE",
      data: projectId,
    });
  };

  const renderMembers = (projectId, members) => {
    return (
      <Link
        key={projectId}
        to={`/workspaces/${projectId}/settings/pipeline?item=member&key=Pipeline%20Settings`}
        className={"flex -space-x-1 overflow-hidden justify-start cursor-pointer"}
      >
        {(members || []).map((item, index) => (
          <img
            key={item.accountId}
            title={item.displayName}
            className={`relative z-${
              index + 1
            } inline-block h-8 w-8 rounded-full ring-2 ring-white`}
            src={item.avatarUrl}
            alt=""
          />
        ))}
      </Link>
    );
  };

  const filterData = useMemo(() => {
    let datas = teamPipelines
      .toJS()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    if (debouncedSearchText) {
      datas = datas.filter(
        (item) =>
          item.name &&
          item.name.trim().toLowerCase().includes(debouncedSearchText.trim().toLowerCase())
      );
    }
    if (currency) {
      datas = datas.filter((item) => item.currency === currency);
    }
    return datas.map((item) => {
      const lead = teamMembers.toJS().find((m) => m.accountId === item.projectManager);
      return {
        pipeline: (
          <Link
            key={item.id}
            to={`/workspaces/${item.id}/settings/pipeline?item=general&key=Pipeline%20Settings`}
            data-testid={`pipeline-row-link-${item.id}`}
            className="block hover:underline"
          >
            {item.name}
          </Link>
        ),
        currency: item.currency,
        lead: lead ? (
          <div className="flex items-center min-w-0 gap-x-2">
            <img
              className="h-8 w-8 flex-none rounded-full bg-gray-50"
              src={lead.avatarUrl}
              alt=""
            />
            <div className="min-w-0 flex-auto">
              <p className="text-sm font-semibold leading-6 text-gray-900">
                {lead.displayName}
                {lead.email ? ` (${lead.email})` : ""}
              </p>
            </div>
          </div>
        ) : null,
        members: renderMembers(item.id, item.members),
        action: (
          <Menu as="div" className="relative flex-none w-max">
            <MenuButton className="block p-2.5 text-gray-500 hover:text-gray-900">
              <EllipsisHorizontalIcon aria-hidden="true" className="size-5" />
            </MenuButton>
            <MenuItems
              transition
              anchor="bottom start"
              className="absolute right-0 z-10 mt-2 w-max origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
            >
              <MenuItem>
                <div
                  onClick={() => {
                    navigate(
                      `/workspaces/${item.id}/settings/pipeline?item=general&key=Pipeline%20Settings`
                    );
                  }}
                  data-testid="pipeline-view-action"
                  className="block px-3 py-1 text-sm/6 text-gray-900 data-focus:bg-gray-50 data-focus:outline-hidden"
                >
                  {t("View")}
                </div>
              </MenuItem>
              {isAdmin ? (
                <MenuItem>
                  <div
                    onClick={() => {
                      handleRemovePipeline(item.id);
                    }}
                    data-testid="pipeline-remove-action"
                    className="block px-3 py-1 text-sm/6 text-red-600 data-focus:bg-gray-50 data-focus:outline-hidden"
                  >
                    {t("Remove")}
                  </div>
                </MenuItem>
              ) : null}
            </MenuItems>
          </Menu>
        ),
      };
    });
  }, [teamPipelines, debouncedSearchText, currency]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    if (showConfirm.type === "REMOVE_PIPELINE") {
      deleteProject(showConfirm.data).then(() => {
        dispatch(TenantAction.getTeamPipelines());
        dispatch(
          LayoutAction.setNotification({
            title: t("Remove pipeline"),
            type: "success",
            message: t("Remove successfully!"),
          })
        );
      });
    }
    setShowConfirm({ show: false, title: "", description: "" });
  };

  return (
    <div className="py-4 max-w-5xl">
      <div className="mb-4 flex items-center justify-between">
        <div className="px-4 sm:px-0">
          <h3 className="text-lg font-semibold text-gray-900">{t("Pipelines")}</h3>
          <p className="mt-1 max-w-2xl text-sm/6 text-gray-500">
            {t("Manage pipelines, and assign members to your pipeline")}
          </p>
        </div>
      </div>
      <div className="py-4">
        <div className="mb-2 flex items-center justify-between">
          <div className="flex items-center">
            <div className="relative w-full mr-2">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
              <input
                type="text"
                placeholder={t("Find pipeline")}
                data-testid="pipeline-search-input"
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg focus:ring-4 focus:ring-blue-100 focus:border-blue-600 text-sm font-medium placeholder-gray-500"
                onChange={(e) => {
                  const newVal = e.target.value || "";
                  setSearchText(newVal);
                }}
              />
            </div>
            <SelectField
              className="w-50 mr-2"
              value={currencyList.find((option) => option.value === currency)}
              onChange={(selectedOption) => setCurrency(selectedOption?.value)}
              options={currencyList}
              placeholder={t("Currency")}
              isClearable
              data-testid="pipeline-currency-filter-select"
              dropdownIndicator={<ChevronDownIcon className="w-3.5 h-3.5 text-gray-500" />}
            />
          </div>
          <div>
            {isAdmin ? (
              <button
                onClick={handAddPipeline}
                data-testid="add-pipeline-button"
                className="flex items-center gap-2 text-white bg-blue-600 rounded-lg px-3 py-2 font-medium cursor-pointer"
              >
                <SquareKanban className="w-4 h-4" />
                {t("Add Pipeline")}
              </button>
            ) : null}
          </div>
        </div>
        <GridTable
          headers={pipelineHeaders}
          rows={filterData}
          pageSize={10}
          data-testid="pipeline-list-table"
        />
      </div>
      <ConfirmDialog
        visible={showConfirm.show}
        title={showConfirm.title}
        description={showConfirm.description}
        alertIcon={showConfirm.alertIcon || <OctagonAlertIcon />}
        dangerBtn={showConfirm.dangerBtn}
        onSubmit={handleSubmit}
        onClose={() => {
          setShowConfirm({ show: false, title: "", description: "" });
        }}
      />
    </div>
  );
};

export default PipelineSetting;
